/**
 * Carte compteur Strasbourg Électricité Réseaux (idtype `elecstrasbourg`,
 * DEV-421) : logique PURE des onglets « Données contractuelles et techniques »
 * et « Données reçues », extraite des composants pour être testée.
 *
 * Contrat : la réponse de dapi `GET /source/informations` (homeys-api-data#237),
 * relevée en staging le 23/09/2026 — blocs `compteur`, `contrat`,
 * `structure_tarifaire` et `data.<variable servie>.<pas>`.
 *
 * INTENTION : un client qui ouvre la carte d'un compteur ÉS voit ce que le
 * gestionnaire de réseau nous a transmis, sous son vrai nom, et rien d'autre.
 *
 * 1. Un champ technique absent s'affiche « - » : aucune valeur par défaut, aucun
 *    segment deviné.
 * 2. Un flux sans donnée n'est pas une erreur de réception : la courbe de charge
 *    n'existe que si le client l'a activée auprès du gestionnaire de réseau (le
 *    3ᵉ compteur pilote ne l'a pas : dapi sert alors `puissance_elec_active.raw`
 *    à bornes nulles). Le flux est dit « Aucune donnée reçue », en information,
 *    jamais en avertissement.
 */
import { formatDate, formatNumber } from "@core/helpers/format.helper"
import type {
    BornesElecStrasbourg,
    DataElecStrasbourg,
    InformationElecStrasbourg,
    LineBlock,
    LineBlockLine,
} from "@types"

type Translate = (key: string) => string

/**
 * Les flux d'un compteur ÉS, dans l'ordre de la carte, lus sous les noms de
 * variables SERVIS par dapi (`/data/source/{id}`) :
 *  - `energie_elec_tout` : énergie journalière et horaire (kWh) ;
 *  - `puissance_elec_active` `raw` : courbe de charge au pas natif 30 min (kW) ;
 *  - `puissance_elec_tout` `daily` : puissance maximale journalière (VA).
 */
export const FLUX_ELECSTRASBOURG = [
    {
        cle: "energie_journaliere",
        variable: "energie_elec_tout",
        grain: "daily",
    },
    { cle: "energie_horaire", variable: "energie_elec_tout", grain: "hourly" },
    {
        cle: "courbe_de_charge",
        variable: "puissance_elec_active",
        grain: "raw",
    },
    { cle: "puissance_max", variable: "puissance_elec_tout", grain: "daily" },
] as const

/** Une borne telle que servie : chaîne ISO (dapi) ou Date (store typé). */
type Borne = string | Date

/** Vrai si la valeur est une borne exploitable (chaîne non vide ou Date valide). */
function estUneBorne(value: unknown): value is Borne {
    if (typeof value === "string") return value !== ""
    return value instanceof Date && !isNaN(value.getTime())
}

function valeurOuTiret(value: string | null | undefined): string {
    return value == null || value === "" ? "-" : value
}

function dateOuTiret(value: unknown): string {
    return estUneBorne(value) ? formatDate(value) : "-"
}

/**
 * Lignes « Données contractuelles et techniques » d'un compteur ÉS.
 *
 * Rend une liste vide quand dapi ne sert aucune information pour ce compteur :
 * l'onglet ne fabrique pas des tirets pour une source inconnue.
 */
export function buildElecStrasbourgTechnicalLines(
    informations: InformationElecStrasbourg | null | undefined,
    t: Translate
): LineBlockLine[] {
    if (!informations) return []
    const compteur = informations.compteur ?? {}
    const contrat = informations.contrat ?? {}
    const tarif = informations.structure_tarifaire ?? {}
    return [
        {
            text: t("elecstrasbourg.segment"),
            value: valeurOuTiret(compteur.segment),
        },
        {
            text: t("elecstrasbourg.puissance_souscrite"),
            value: formatNumber(
                tarif.puissance_souscrite_soutirage?.puissance,
                {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                    unit: "kVA",
                }
            ),
        },
        {
            text: t("elecstrasbourg.tarif_acheminement"),
            value: valeurOuTiret(tarif.tarif_acheminement),
            tooltip: t("elecstrasbourg.tooltip.tarif_acheminement"),
        },
        {
            text: t("elecstrasbourg.heures_creuses"),
            value: valeurOuTiret(tarif.heures_creuses),
        },
        {
            text: t("elecstrasbourg.type_contrat"),
            value: valeurOuTiret(contrat.type_contrat),
        },
        {
            text: t("elecstrasbourg.statut_contrat"),
            value: valeurOuTiret(contrat.statut_contrat),
        },
        {
            text: t("elecstrasbourg.date_derniere_activation"),
            value: dateOuTiret(contrat.date_derniere_activation),
        },
        {
            text: t("elecstrasbourg.date_dernier_changement_tarif"),
            value: dateOuTiret(contrat.date_dernier_changement_tarif),
        },
        {
            text: t("elecstrasbourg.type_compteur"),
            value: valeurOuTiret(compteur.type_compteur),
        },
        {
            text: t("elecstrasbourg.statut_point"),
            value: valeurOuTiret(compteur.statut_point),
        },
    ]
}

/** Bornes d'un flux, ou null quand dapi ne sert pas ce pas. */
function bornesDuFlux(
    data: DataElecStrasbourg | null | undefined,
    variable: string,
    grain: string
): BornesElecStrasbourg | null {
    const bloc = (data as Record<string, unknown> | null | undefined)?.[
        variable
    ]
    if (!bloc || typeof bloc !== "object") return null
    const slot = (bloc as Record<string, unknown>)[grain]
    if (!slot || typeof slot !== "object") return null
    return slot as BornesElecStrasbourg
}

/**
 * Onglet « Données reçues » d'un compteur ÉS : un bloc par flux, qui dit ce qui
 * est détenu (première et dernière donnée), jamais une fraîcheur qu'il ne mesure
 * pas.
 */
export function buildElecStrasbourgReceivedData(
    informations: InformationElecStrasbourg | null | undefined,
    t: Translate
): LineBlock[] {
    return FLUX_ELECSTRASBOURG.map((flux) => {
        const bornes = bornesDuFlux(
            informations?.data,
            flux.variable,
            flux.grain
        )
        const recu = estUneBorne(bornes?.date_from)
        return {
            id: flux.cle,
            title: [
                t("elecstrasbourg.flux.categorie"),
                t(`elecstrasbourg.flux.${flux.cle}.titre`),
            ],
            badge: [
                recu
                    ? {
                          text: t("elecstrasbourg.flux.recu"),
                          icon: "pi pi-check-circle",
                          status: "success" as const,
                      }
                    : {
                          text: t("elecstrasbourg.flux.non_recu"),
                          icon: "pi pi-info-circle",
                          status: "info" as const,
                          tooltip: t(
                              `elecstrasbourg.flux.${flux.cle}.non_recu_tooltip`
                          ),
                      },
            ],
            lines: [
                {
                    text: t("elecstrasbourg.flux.pas"),
                    value: t(`elecstrasbourg.flux.${flux.cle}.pas`),
                },
                {
                    text: t("elecstrasbourg.flux.premiere_donnee"),
                    value: dateOuTiret(bornes?.date_from),
                },
                {
                    text: t("elecstrasbourg.flux.derniere_donnee"),
                    value: dateOuTiret(bornes?.date_to),
                },
            ],
        }
    })
}

/**
 * Les variables DÉTENUES pour un compteur ÉS, telles que la page de données les
 * attend pour choisir ses graphiques (`sourceStore.getVariablesBySourceId`).
 *
 * dapi sert `data._variables: null` pour ÉS et porte les bornes directement
 * sous `data.<variable>.<pas>`. Sans cette lecture, la page de données ne
 * trouvait AUCUNE variable et masquait tous les graphiques `elecstrasbourg_*`.
 * Une variable n'est retenue que si au moins un de ses pas a une première
 * donnée : le 3ᵉ compteur pilote, sans courbe de charge, n'a donc pas
 * `puissance_elec_active`, et sa carte « courbe de charge » disparaît sans
 * message au lieu d'afficher un cadre vide.
 */
export function variablesDetenuesElecStrasbourg(
    informations: InformationElecStrasbourg | null | undefined
): string[] {
    const data = informations?.data as
        | Record<string, unknown>
        | null
        | undefined
    if (!data) return []
    return Object.entries(data)
        .filter(([cle, bloc]) => {
            if (cle.startsWith("_") || !bloc || typeof bloc !== "object")
                return false
            return Object.values(bloc as Record<string, unknown>).some(
                (slot) =>
                    !!slot &&
                    typeof slot === "object" &&
                    estUneBorne((slot as BornesElecStrasbourg).date_from)
            )
        })
        .map(([cle]) => cle)
}
